import React from 'react';
import { Book, Code, Phone, Bot, Zap, Database, Globe, Mic, ArrowRight, ExternalLink, Play } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const docSections = [
  {
    title: 'Getting Started',
    description: 'Learn how to install and set up the AIConneqtors SDK',
    icon: Book,
    href: '#getting-started',
    color: 'from-blue-500 to-blue-600'
  },
  {
    title: 'Client',
    description: 'Initialize and configure the AIConneqtors client',
    icon: Code,
    href: '#client',
    color: 'from-green-500 to-green-600'
  },
  {
    title: 'Agent',
    description: 'Create, manage, and customize AI agents',
    icon: Bot,
    href: '#agent',
    color: 'from-purple-500 to-purple-600'
  },
  {
    title: 'Call',
    description: 'Manage call logs and dispatch calls',
    icon: Phone,
    href: '#call',
    color: 'from-orange-500 to-orange-600'
  },
  {
    title: 'Simulation',
    description: 'Test and optimize your agents with automated scenarios',
    icon: Zap,
    href: '#simulation',
    color: 'from-yellow-500 to-yellow-600'
  },
  {
    title: 'Integrations',
    description: 'Connect with external services and APIs',
    icon: Globe,
    href: '#integrations',
    color: 'from-pink-500 to-pink-600'
  },
  {
    title: 'Knowledge Base',
    description: 'Manage files and knowledge for your agents',
    icon: Database,
    href: '#knowledge-base',
    color: 'from-indigo-500 to-indigo-600'
  },
  {
    title: 'Voices & Languages',
    description: 'Explore 1000+ voices and 90+ supported languages',
    icon: Mic,
    href: '#voices',
    color: 'from-teal-500 to-teal-600'
  }
];

const Documentation = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-6 text-center mb-16">
          <div className="inline-block px-4 py-1.5 bg-teal-400/10 border border-teal-400/30 rounded-full text-teal-400 text-sm font-medium mb-6">
            Developer Documentation
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            AIConneqtors <span className="text-teal-400">SDK</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            Build powerful AI voice agents with our easy-to-use SDK
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="#getting-started"
              className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Get Started
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#1a1a1f] hover:bg-[#252530] text-white px-6 py-3 rounded-lg font-semibold border border-gray-700 transition-colors"
            >
              View SDK on GitHub
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Video Tutorial Section */}
        <div className="max-w-4xl mx-auto px-6 mb-20">
          <div className="bg-[#0d1117] border border-gray-800 rounded-2xl overflow-hidden">
            <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center relative group cursor-pointer">
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors"></div>
              <div className="w-20 h-20 bg-teal-400 rounded-full flex items-center justify-center z-10 group-hover:scale-110 transition-transform">
                <Play className="w-8 h-8 text-black ml-1" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <span className="text-teal-400 text-sm font-medium">Video Tutorial</span>
                <h3 className="text-xl font-semibold text-white">Introducing AIConneqtors</h3>
                <p className="text-gray-400 text-sm">Discover how AIConneqtors can transform your voice AI workflows.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Documentation Sections */}
        <div className="max-w-7xl mx-auto px-6 mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {docSections.map((section, index) => (
              <a
                key={index}
                href={section.href}
                className="group bg-[#0d1117] border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-all hover:scale-105"
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${section.color} flex items-center justify-center mb-4`}>
                  <section.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-teal-400 transition-colors">
                  {section.title}
                </h3>
                <p className="text-gray-500 text-sm">{section.description}</p>
              </a>
            ))}
          </div>
        </div>

        {/* Code Example Section */}
        <div className="max-w-4xl mx-auto px-6 mb-20" id="getting-started">
          <h2 className="text-2xl font-bold text-white mb-6">Quick Start</h2>
          <div className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-[#1a1a1f] border-b border-gray-800">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span className="text-gray-500 text-sm ml-2">installation.sh</span>
            </div>
            <pre className="p-6 text-sm overflow-x-auto">
              <code className="text-gray-300">
{`# Install the SDK
pip install aiconneqtors

# Initialize in your code
from aiconneqtors import Client

client = Client(api_key="your_api_key")

# Create an agent
agent = client.agents.create(
    name="Customer Support Bot",
    voice="alloy",
    greeting="Hello! How can I help you today?"
)

# Start a call
call = client.calls.create(
    agent_id=agent.id,
    to_number="+1234567890"
)`}
              </code>
            </pre>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-r from-[#0d1117] to-[#1a1a1f] border border-gray-800 rounded-2xl p-8 text-center">
            <span className="text-teal-400 text-sm font-medium">Developer API</span>
            <h2 className="text-2xl font-bold text-white mt-2 mb-4">
              Ready to build your AI voice agent?
            </h2>
            <p className="text-gray-400 mb-6">
              Get started with AIConneqtors SDK today and create powerful AI voice experiences for your users.
            </p>
            <a
              href="/signup"
              className="inline-flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Start Building
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Documentation;
